<template>
    <component
        :is="tag"
        class="activix-radio-group inline-flex flex-wrap"
        role="radiogroup"
        ref="groupRef"
        @keydown="handleKeydown"
    >
        <slot />
    </component>
</template>

<script setup lang="ts">
    import { computed, provide, onMounted, ref, nextTick } from 'vue';
    import { v4 as uuidv4 } from 'uuid';
    import { RadioSize } from './types.ts';
    import type { RadioGroupContext } from './types.ts';

    interface Props {
        value?: any;
        size?: RadioSize;
        fill?: string;
        textColor?: string;
        disabled?: boolean;
        tag?: string;
        name?: string;
    }

    const props = withDefaults(defineProps<Props>(), {
        disabled: false,
        tag: 'div',
    });

    const emit = defineEmits<{
        (e: 'input', value: any): void;
        (e: 'change', value: any): void;
        (e: 'handleChange', value: any): void;
    }>();

    const groupRef = ref<HTMLElement | null>(null);

    // Generate unique name for radio group if not provided
    const generatedName = `activix-radio-group-${uuidv4()}`;
    const radioGroupName = computed(() => props.name || generatedName);

    // Method for child radios to update group value
    const changeValue = (value: any) => {
        emit('input', value);
    };

    // Provide context to child radios
    const radioGroupContext: RadioGroupContext = {
        value: computed(() => props.value),
        disabled: props.disabled,
        size: props.size,
        fill: props.fill,
        textColor: props.textColor,
        name: radioGroupName.value,
        changeValue,
    };

    provide('radioGroup', radioGroupContext);

    // Keyboard navigation
    const KEY_CODE = {
        LEFT: 37,
        UP: 38,
        RIGHT: 39,
        DOWN: 40,
    };

    const handleKeydown = (e: KeyboardEvent) => {
        const target = e.target as HTMLElement;

        const roleRadios = Array.from(
            groupRef.value?.querySelectorAll<HTMLElement>('[data-role="activix-radio-item"]') || []
        );
        const length = roleRadios.length;
        const index = roleRadios.indexOf(target);
        if (length === 0 || index < 0) return;

        switch (e.keyCode) {
            case KEY_CODE.LEFT:
            case KEY_CODE.UP:
                e.stopPropagation();
                e.preventDefault();
                if (index === 0) {
                    roleRadios[length - 1].click();
                    roleRadios[length - 1].focus();
                } else {
                    roleRadios[index - 1].click();
                    roleRadios[index - 1].focus();
                }
                break;
            case KEY_CODE.RIGHT:
            case KEY_CODE.DOWN:
                e.stopPropagation();
                e.preventDefault();
                if (index === length - 1) {
                    roleRadios[0].click();
                    roleRadios[0].focus();
                } else {
                    roleRadios[index + 1].click();
                    roleRadios[index + 1].focus();
                }
                break;
            default:
                break;
        }
    };

    // Set first radio tabIndex to 0 if none selected (for Tab navigation)
    onMounted(() => {
        nextTick(() => {
            if (groupRef.value) {
                const radios = groupRef.value.querySelectorAll<HTMLInputElement>('[type=radio]');
                const firstLabel = groupRef.value.querySelector<HTMLElement>(
                    '[data-role="activix-radio-item"]'
                );
                const anyChecked = Array.from(radios).some((radio) => radio.checked);
                if (!anyChecked && firstLabel) {
                    firstLabel.tabIndex = 0;
                }
            }
        });
    });

    // Expose for template ref
    defineExpose({ groupRef });
</script>

<script lang="ts">
    export default {
        name: 'ActivixRadioGroup',
    };
</script>
